import { Component } from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Prioritization } from "@Core/models/codegen/configuration.models";
import { TenantService } from '@Core/services/codegen/tenant.service';
import { TenantContext } from '@Core/contexts/tenant-context';
import { TenantAssignableService } from '@Services/tenant-assignable.service';

import { SmAssignableBadgeVM } from "@Shared/Components/sm-assignable-badge/sm-assignable-badge.viewmodel";
import { PrioritizationConfirmModalComponent } from "./prioritization-confirm-modal.component";
import { TimerType } from '@Core/enums/timer-type.enum';

@Component({
    standalone: false,
    selector: 'prioritization',
    templateUrl: './prioritization.component.html',
    styleUrls: ['./prioritization.component.scss']
})
export class PrioritizationComponent {

    public prioritization: Prioritization;
    public allPrioritizationCards: PrioritizationCardVM[];
    public currentPrioritizationCards: PrioritizationCardVM[];
    public originalPrioritizationCards: PrioritizationCardVM[];
    public imminentList: ImminentVM[] = [];
    public orderChanges: PrioritizationChangeOrderVM[] = [];
    public timerChanges: PrioritizationChangeTimerVM[] = [];

    public lastUpdated: Date = new Date();
    public lastUpdatedBy: Observable<SmAssignableBadgeVM>;
    public ngUnsubscribe: Subject<boolean> = new Subject();
    public loading: boolean = true;

    constructor(public tenantService: TenantService,
        public tenantContext: TenantContext,
        public tenantAssignableService: TenantAssignableService,
        public modalService: NgbModal) {}

    public ngOnInit() {
        this.loading = true;

        this.allPrioritizationCards = PrioritizationCards;

        this.tenantService.RetrievePrioritization(this.tenantContext)
            .subscribe(() => {
                this.loading = false;
            });

        this.tenantContext.getStore<Prioritization>(new Prioritization()).values
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((data: Prioritization[]) => {
                if (data?.length) {
                    this.prioritization = data[0];
                    this.convertPrioritizationToVMs();
                }
            });
    }

    public convertPrioritizationToVMs() {
        this.imminentList = [
            { 
                Name: "Deadline", 
                Amount: this.prioritization.DeadlineImminentValue, 
                Units: this.prioritization.DeadlineImminentUnitOfTime 
            },
            { 
                Name: "Service Level", 
                Amount: this.prioritization.ServiceLevelImminentValue, 
                Units: this.prioritization.ServiceLevelImminentUnitOfTime
            },
            { 
                Name: "Due Date", 
                Amount: this.prioritization.DueDateImminentValue, 
                Units: this.prioritization.DueDateImminentUnitOfTime
            }
        ];

        this.currentPrioritizationCards = _.map(this.prioritization.PrioritizationTypeOrder, t => {
            return _.find(this.allPrioritizationCards, c => c.Id == t);
        });

        this.originalPrioritizationCards = _.clone(this.currentPrioritizationCards);

        this.lastUpdated = this.prioritization.data.LastUpdatedTimeUTC;
        this.lastUpdatedBy = this.tenantAssignableService.getAssignableAsBadge(this.prioritization.LastUpdatedBy);
    }

    public convertVMToPrioritization(): Prioritization {
        var prioritizationToSave = _.clone(this.prioritization);

        var deadline = _.find(this.imminentList, i => i.Name == "Deadline");
        prioritizationToSave.DeadlineImminentValue = deadline.Amount;
        prioritizationToSave.DeadlineImminentUnitOfTime = deadline.Units;

        var serviceLevel = _.find(this.imminentList, i => i.Name == "Service Level");
        prioritizationToSave.ServiceLevelImminentValue = serviceLevel.Amount;
        prioritizationToSave.ServiceLevelImminentUnitOfTime = serviceLevel.Units;

        var dueDate = _.find(this.imminentList, i => i.Name == "Due Date");
        prioritizationToSave.DueDateImminentValue = dueDate.Amount;
        prioritizationToSave.DueDateImminentUnitOfTime = dueDate.Units;

        prioritizationToSave.PrioritizationTypeOrder = _.map(this.currentPrioritizationCards, c => c.Id);

        return prioritizationToSave;
    }

    public ngOnDestroy() {
        if (this.ngUnsubscribe)
            this.ngUnsubscribe.unsubscribe();
    }

    public moveUp(card: PrioritizationCardVM) {
        var indexOf = _.findIndex(this.currentPrioritizationCards, c => c.Id == card.Id);
        this.currentPrioritizationCards.splice(indexOf, 1);
        this.currentPrioritizationCards.splice(indexOf-1, 0, card);
        this.priorityOrderChanged(card);
    }

    public moveDown(card: PrioritizationCardVM) {
        var indexOf = _.findIndex(this.currentPrioritizationCards, c => c.Id == card.Id);
        this.currentPrioritizationCards.splice(indexOf, 1);
        this.currentPrioritizationCards.splice(indexOf+1, 0, card);
        this.priorityOrderChanged(card);
    }

    public imminentAmountChanged(timer: ImminentVM) {
        var beforeAmount;
        var units;

        if (timer.Name === "Deadline") {
            beforeAmount = this.prioritization?.DeadlineImminentValue;
            units = this.prioritization?.DeadlineImminentUnitOfTime;
        } else if (timer.Name === "Service Level") {
            beforeAmount = this.prioritization?.ServiceLevelImminentValue;
            units = this.prioritization?.ServiceLevelImminentUnitOfTime;
        } else if (timer.Name === "Due Date") {
            beforeAmount = this.prioritization?.DueDateImminentValue;
            units = this.prioritization?.DueDateImminentUnitOfTime;
        }

        if ((timer.Amount != beforeAmount)
            && !_.some(this.timerChanges, tc => tc.Type === timer.Name)) {

            var timerChange = {
                Type: timer.Name,
                AmountBefore: beforeAmount,
                AmountAfter: timer.Amount,
                UnitsBefore: units,
                UnitsAfter: units
            };

            this.timerChanges.push(timerChange);
        }
        else if ((timer.Amount != beforeAmount)
            && _.some(this.timerChanges, tc => tc.Type === timer.Name)) {

            var indexToUpdate = _.findIndex(this.timerChanges, tc => tc.Type === timer.Name);
            this.timerChanges[indexToUpdate].AmountBefore = beforeAmount;
            this.timerChanges[indexToUpdate].AmountAfter = timer.Amount;
        }
        else {
            this.timerChanges = _.filter(this.timerChanges, tc => tc.Type != timer.Name);
        }
    }

    public imminentUnitsChanged(timer: ImminentVM) {
        var amount;
        var beforeUnits;

        if (timer.Name === "Deadline") {
            beforeUnits = this.prioritization?.DeadlineImminentUnitOfTime;
            amount = this.prioritization?.DeadlineImminentValue;
        } else if (timer.Name === "Service Level") {
            beforeUnits = this.prioritization?.ServiceLevelImminentUnitOfTime;
            amount = this.prioritization?.ServiceLevelImminentValue;
        } else if (timer.Name === "Due Date") {
            beforeUnits = this.prioritization?.DueDateImminentUnitOfTime;
            amount = this.prioritization?.DueDateImminentValue;
        }

        if ((timer.Units != beforeUnits)
            && !this.timerChanges.some(tc => tc.Type == timer.Name)) {

            var timerChange = {
                Type: timer.Name,
                AmountBefore: amount,
                AmountAfter: amount,
                UnitsBefore: beforeUnits,
                UnitsAfter: timer.Units
            };

            this.timerChanges.push(timerChange);
        }
        else if ((timer.Units != beforeUnits)
            && this.timerChanges.some(tc => tc.Type === timer.Name)) {
            
            var indexToUpdate = _.findIndex(this.timerChanges, tc => tc.Type === timer.Name);
            this.timerChanges[indexToUpdate].UnitsBefore = beforeUnits;
            this.timerChanges[indexToUpdate].UnitsAfter = timer.Units;
        }
        else {
            this.timerChanges = _.filter(this.timerChanges, tc => tc.Type != timer.Name);
        }
    }

    public canSave(): boolean {
        if (_.some(this.imminentList, i => !i.Amount)
                || (this.orderChanges.length == 0
                    && this.timerChanges.length == 0)) {
            return false;
        }

        return true;
    }

    public confirmSave(): void {
        const options: NgbModalOptions =  { ariaLabelledBy: 'modal-basic-title', backdrop: "static", size: "md"};
        const modalRef = this.modalService.open(PrioritizationConfirmModalComponent, options);
        modalRef.componentInstance.orderChanges = this.orderChanges;
        modalRef.componentInstance.timerChanges = this.timerChanges;
       
        modalRef.result.then(
            (result) => {
                this.onSave();
            },
            (error)=>{
                //User dismissed the modal dialog
            }
        );
    }

    public onSave(): void {
        var prioritizationToSave = this.convertVMToPrioritization();

        this.tenantService.ModifyPrioritization(prioritizationToSave.serialize(), this.tenantContext).subscribe(() => {});
        this.clearAllChanges();
    }

    public onCancel(): void {
        this.convertPrioritizationToVMs();
        this.clearAllChanges();
    }

    public isValidDownState(index: number) {
        const nextCardIndex = index + 1;

        if (!this.currentPrioritizationCards.length || nextCardIndex >= this.currentPrioritizationCards.length)
            return false;

        const nextCard = this.currentPrioritizationCards[nextCardIndex];
        const currCard = this.currentPrioritizationCards[index];

        if (currCard.TimerType == nextCard.TimerType){
            return currCard.PriorityType >= nextCard.PriorityType
        } else {
            return true;
        }
    }

    public isValidUpState(index: number): boolean {
        const prevCardIndex = index - 1;
    
        if (!this.currentPrioritizationCards.length || prevCardIndex < 0)
            return false;
    
        const prevCard = this.currentPrioritizationCards[prevCardIndex];
        const currCard = this.currentPrioritizationCards[index];
    
        if (currCard.TimerType === prevCard.TimerType) {
            return currCard.PriorityType <= prevCard.PriorityType;
        } else {
            return true;
        }
    }

    private priorityOrderChanged(card: PrioritizationCardVM) {
        this.orderChanges = _.filter(this.orderChanges, oc => oc.Type != card.Name);

        var priorIndex = _.findIndex(this.originalPrioritizationCards, opc => opc.Name == card.Name);
        var currentIndex = _.findIndex(this.currentPrioritizationCards, cpc => cpc.Name == card.Name);

        var orderChange = {
            Type: card.Name,
            PriorityBefore: priorIndex + 1,
            PriorityAfter: currentIndex + 1
        };
        this.orderChanges.push(orderChange);
    }

    private clearAllChanges() {
        this.orderChanges = [];
        this.timerChanges = [];
    }
};

interface PrioritizationCardVM {
    Id: string; // The value stored in the database
    Name: string;
    Priority: 'late' | 'imminent' | 'pending';
    PriorityType: PriorityType;
    TimerType: TimerType;
}

interface ImminentVM {
    Name: string;
    Amount: number;
    Units: string;
}

export interface PrioritizationChangeOrderVM {
    Type: string;
    PriorityBefore: number;
    PriorityAfter: number;
}

export interface PrioritizationChangeTimerVM {
    Type: string;
    AmountBefore: number;
    AmountAfter: number;
    UnitsBefore: string;
    UnitsAfter:string;
}

export enum PriorityType {
    Late = 0,
    Imminent = 1,
    Pending = 2
}

export const PrioritizationCards: PrioritizationCardVM[] = [
    {
        Id: "LateDeadline",
        Name: "Late Deadline",
        Priority: 'late',
        PriorityType: PriorityType.Late,
        TimerType: TimerType.Deadline
    },
    {
        Id: "LateServiceLevel",
        Name: "Late Service Level",
        Priority: 'late',
        PriorityType: PriorityType.Late,
        TimerType: TimerType.SLA
    },
    {
        Id: "LateDueDate",
        Name: "Late Due Date",
        Priority: 'late',
        PriorityType: PriorityType.Late,
        TimerType: TimerType.DueDate
    },
    {
        Id: "ImminentDeadline",
        Name: "Imminent Deadline",
        Priority: 'imminent',
        PriorityType: PriorityType.Imminent,
        TimerType: TimerType.Deadline
    },
    {
        Id: "ImminentServiceLevel",
        Name: "Imminent Service Level",
        Priority: 'imminent',
        PriorityType: PriorityType.Imminent,
        TimerType: TimerType.SLA
    },
    {
        Id: "ImminentDueDate",
        Name: "Imminent Due Date",
        Priority: 'imminent',
        PriorityType: PriorityType.Imminent,
        TimerType: TimerType.DueDate
    },
    {
        Id: "PendingDeadline",
        Name: "Pending Deadline",
        Priority: 'pending',
        PriorityType: PriorityType.Pending,
        TimerType: TimerType.Deadline
    },
    {
        Id: "PendingServiceLevel",
        Name: "Pending Service Level",
        Priority: 'pending',
        PriorityType: PriorityType.Pending,
        TimerType: TimerType.SLA
    },
    {
        Id: "PendingDueDate",
        Name: "Pending Due Date",
        Priority: 'pending',
        PriorityType: PriorityType.Pending,
        TimerType: TimerType.DueDate
    }
];